// src/pages/AdminFichajes.js
import React, { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useFichajes } from '../hooks/useFichajes';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { formatHora, formatHoras } from '../utils/formatos';
import PantallaCargaMedica from '../components/common/PantallaCargaMedica';
import { Modal } from '../components/common/ui';

// ISO → valor para <input type="datetime-local"> en hora LOCAL (YYYY-MM-DDTHH:mm)
const isoAInputLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

// Tarjeta de estadística reutilizable
function StatCard({ label, valor, sub, icon, acento = 'sky' }) {
    const acentos = {
        sky: 'text-sky-600 bg-sky-50 border-sky-100',
        emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
        indigo: 'text-indigo-600 bg-indigo-50 border-indigo-100',
        amber: 'text-amber-600 bg-amber-50 border-amber-100'
    };
    return (
        <div className="bg-white border border-slate-200/80 rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-4">
            <div className={`flex items-center justify-center w-12 h-12 rounded-2xl border ${acentos[acento]}`}>
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
                <p className="text-2xl font-black text-slate-900 tracking-tight leading-tight">{valor}</p>
                {sub && <p className="text-[11px] font-bold text-slate-400 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

export default function AdminFichajes() {
    const {
        fichajes, loading, cargarFichajes, isGlobalAdmin,
        fechaFiltro, setFechaFiltro,
        medicoFiltro, setMedicoFiltro,
        medicosDisponibles,
        estadisticas
    } = useFichajes();

    const { user } = useAuth();
    const { showToast } = useToast();
    // Roles que gestionan jornadas (cerrar/corregir): administración y RRHH (recepción)
    const puedeGestionar = ['admin', 'supermedico', 'recepcion'].includes(user?.role || user?.rol);
    const [cerrandoId, setCerrandoId] = useState(null);

    // Corrección de jornada (validación RRHH)
    const [editando, setEditando] = useState(null); // fichaje en edición
    const [form, setForm] = useState({ entrada: '', salida: '', motivo: '' });
    const [guardando, setGuardando] = useState(false);

    const abrirCorreccion = (fichaje) => {
        setForm({
            entrada: isoAInputLocal(fichaje.entrada),
            salida: isoAInputLocal(fichaje.salida) || isoAInputLocal(new Date().toISOString()),
            motivo: ''
        });
        setEditando(fichaje);
    };

    const guardarCorreccion = async () => {
        if (!editando) return;
        if (!form.entrada || !form.salida) { showToast('Indica entrada y salida.', 'error'); return; }
        try {
            setGuardando(true);
            await api.patch(`/fichajes/${editando.id || editando._id}/corregir`, {
                entrada: new Date(form.entrada).toISOString(),
                salida: new Date(form.salida).toISOString(),
                motivo: form.motivo
            });
            showToast('Jornada corregida y validada.', 'success');
            setEditando(null);
            cargarFichajes();
        } catch (err) {
            showToast(err.message || 'No se pudo corregir la jornada.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    const hoyStr = new Date().toISOString().split('T')[0];

    // Cierre forzado de un turno olvidado (solo admin/supermédico)
    const handleCerrarTurno = async (fichaje) => {
        const id = fichaje.id || fichaje._id;
        const nombre = `${fichaje.usuario?.nombre || ''} ${fichaje.usuario?.apellidos || ''}`.trim();
        if (!window.confirm(`¿Cerrar el turno abierto de ${nombre}?\nSi excede la jornada legal, la salida se topará a 8h.`)) return;
        try {
            setCerrandoId(id);
            await api.post(`/fichajes/${id}/cerrar`);
            showToast('Turno cerrado correctamente.', 'success');
            cargarFichajes();
        } catch (err) {
            showToast(err.message || 'No se pudo cerrar el turno.', 'error');
        } finally {
            setCerrandoId(null);
        }
    };

    // Pantalla contextual en la primera carga del monitor de presencia
    if (loading && fichajes.length === 0) {
        return (
            <div className="min-h-screen p-4 md:p-8 bg-slate-50/50">
                <PantallaCargaMedica variante="presencia" />
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-8 bg-slate-50/50 animate-fade-in">

            {/* ============ CABECERA ============ */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-slate-900 text-white">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        </span>
                        {isGlobalAdmin ? 'Monitor de Presencia' : 'Mis Fichajes'}
                    </h1>
                    <p className="text-slate-500 font-medium mt-1 ml-1">
                        Control de jornadas laborales en tiempo real.
                    </p>
                </div>

                {/* Píldora de presencia en vivo */}
                {isGlobalAdmin && (
                    <div className="flex items-center gap-2.5 self-start xl:self-auto bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">
                        <span className="relative flex h-3 w-3">
                            <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${estadisticas.activosAhora > 0 ? 'bg-emerald-400 animate-ping' : 'bg-slate-300'}`}></span>
                            <span className={`relative inline-flex rounded-full h-3 w-3 ${estadisticas.activosAhora > 0 ? 'bg-emerald-500' : 'bg-slate-300'}`}></span>
                        </span>
                        <span className="text-sm font-black text-slate-700">
                            {estadisticas.activosAhora} {estadisticas.activosAhora === 1 ? 'profesional' : 'profesionales'} en turno
                        </span>
                    </div>
                )}
            </div>

            {/* ============ ESTADÍSTICAS ============ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <StatCard
                    label="Trabajado hoy" acento="sky"
                    valor={formatHoras(estadisticas.minutosHoy)}
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                />
                <StatCard
                    label="Esta semana" acento="indigo"
                    valor={formatHoras(estadisticas.minutosSemana)}
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                />
                <StatCard
                    label="Este mes" acento="emerald"
                    valor={formatHoras(estadisticas.minutosMes)}
                    sub={`${estadisticas.sesionesMes} ${estadisticas.sesionesMes === 1 ? 'jornada' : 'jornadas'}`}
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>}
                />
                <StatCard
                    label={isGlobalAdmin ? 'Activos ahora' : 'Estado actual'} acento="amber"
                    valor={isGlobalAdmin ? estadisticas.activosAhora : (estadisticas.activosAhora > 0 ? 'En turno' : 'Fuera')}
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z"/></svg>}
                />
            </div>

            {/* ============ FILTROS ============ */}
            <div className="bg-white p-4 md:p-5 rounded-3xl shadow-sm border border-slate-200/80 mb-6 flex flex-wrap items-end gap-4">
                {/* Calendario de fecha */}
                <div className="flex-1 min-w-[180px]">
                    <label htmlFor="fechaFiltro" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Ver día</label>
                    <div className="relative">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400 pointer-events-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                        </span>
                        <input
                            id="fechaFiltro" type="date" value={fechaFiltro} max={hoyStr}
                            onChange={(e) => setFechaFiltro(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-3 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15 transition-all"
                        />
                    </div>
                </div>

                {/* Selector de profesional (solo admins) */}
                {isGlobalAdmin && (
                    <div className="flex-1 min-w-[180px]">
                        <label htmlFor="medicoFiltro" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Profesional</label>
                        <select
                            id="medicoFiltro" value={medicoFiltro} onChange={(e) => setMedicoFiltro(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15 transition-all"
                        >
                            <option value="todos">Todos los profesionales</option>
                            {medicosDisponibles.map((m) => (
                                <option key={m.id} value={m.id}>{m.nombre}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Acciones rápidas */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setFechaFiltro(hoyStr)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${fechaFiltro === hoyStr ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    >
                        Hoy
                    </button>
                    <button
                        onClick={cargarFichajes} disabled={loading}
                        title="Recargar"
                        className="flex items-center justify-center w-[46px] h-[46px] rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-clinica-primary hover:border-clinica-primary/40 transition-all disabled:opacity-50"
                    >
                        <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                    </button>
                </div>

                {/* Fecha seleccionada en texto legible */}
                <div className="w-full xl:w-auto xl:ml-auto text-right">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mostrando</p>
                    <p className="text-sm font-black text-slate-700 capitalize">
                        {fechaFiltro ? format(new Date(`${fechaFiltro}T00:00:00`), "EEEE, d 'de' MMMM", { locale: es }) : '—'}
                    </p>
                </div>
            </div>

            {/* ============ TABLA ============ */}
            <div className="bg-white border border-slate-200/80 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[820px]">
                        <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-6 py-5 text-[11px] font-black tracking-widest text-slate-400 uppercase">Empleado</th>
                            <th className="px-6 py-5 text-[11px] font-black tracking-widest text-slate-400 uppercase text-center">Entrada</th>
                            <th className="px-6 py-5 text-[11px] font-black tracking-widest text-slate-400 uppercase text-center">Salida</th>
                            <th className="px-6 py-5 text-[11px] font-black tracking-widest text-slate-400 uppercase text-center">Jornada</th>
                            <th className="px-6 py-5 text-[11px] font-black tracking-widest text-slate-400 uppercase text-center">Estado</th>
                            <th className="px-6 py-5 text-[11px] font-black tracking-widest text-slate-400 uppercase text-center">Auditoría</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/80">
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <tr key={i} className="animate-pulse bg-white">
                                    <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-slate-100 rounded-2xl"></div><div className="h-4 bg-slate-100 rounded-md w-32"></div></div></td>
                                    <td className="px-6 py-4"><div className="h-6 bg-slate-100 rounded-lg w-16 mx-auto"></div></td>
                                    <td className="px-6 py-4"><div className="h-6 bg-slate-100 rounded-lg w-16 mx-auto"></div></td>
                                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded-md w-16 mx-auto"></div></td>
                                    <td className="px-6 py-4"><div className="h-6 bg-slate-100 rounded-full w-20 mx-auto"></div></td>
                                    <td className="px-6 py-4"><div className="h-6 bg-slate-100 rounded-md w-20 mx-auto"></div></td>
                                </tr>
                            ))
                        ) : fichajes.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-20 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-slate-300">
                                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                        </div>
                                        <h3 className="text-slate-700 font-black text-lg">Sin registros este día</h3>
                                        <p className="text-slate-400 text-sm font-medium">No hay fichajes para la fecha seleccionada.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            fichajes.map((fichaje) => {
                                const mins = fichaje.entrada
                                    ? Math.max(0, Math.round(((fichaje.salida ? new Date(fichaje.salida) : new Date()) - new Date(fichaje.entrada)) / 60000))
                                    : 0;
                                return (
                                    <tr key={fichaje.id || fichaje._id} className="hover:bg-slate-50/80 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center justify-center w-10 h-10 font-black text-white bg-slate-900 rounded-2xl group-hover:scale-105 transition-transform">
                                                    {fichaje.usuario?.nombre?.[0] || 'U'}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="block font-bold text-slate-900 truncate">
                                                        {fichaje.usuario?.nombre} {fichaje.usuario?.apellidos}
                                                    </span>
                                                    {fichaje.descansoInsuficiente ? (
                                                        <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200 text-[10px] font-black uppercase tracking-wide"
                                                              title={`Descanso entre jornadas de ${fichaje.horasDescansoPrevio ?? '<12'}h, por debajo del mínimo legal.`}>
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                                            Descanso {fichaje.horasDescansoPrevio ?? '<12'}h
                                                        </span>
                                                    ) : fichaje.dispositivo?.ip ? (
                                                        <span className="block text-[11px] font-mono text-slate-400 truncate">
                                                            {String(fichaje.dispositivo.ip).split(':')[0]}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            <span className="inline-flex items-center px-3 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-black border border-emerald-100">
                                                {formatHora(fichaje.entrada)}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            {fichaje.salida ? (
                                                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-rose-50 text-rose-700 text-sm font-black border border-rose-100">
                                                    {formatHora(fichaje.salida)}
                                                </span>
                                            ) : <span className="text-slate-300 font-bold">--:--</span>}
                                        </td>

                                        <td className="px-6 py-4 text-center font-black text-slate-700">
                                            {fichaje.estado === 'abierto'
                                                ? <span className="text-clinica-primary">{formatHoras(mins)}<span className="text-[10px] text-slate-400 ml-1">en curso</span></span>
                                                : formatHoras(mins)}
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            {fichaje.estado === 'abierto' ? (
                                                <div className="inline-flex flex-col items-center gap-1.5">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-sky-50 text-sky-700 border border-sky-200">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse"></span>En Turno
                                                    </span>
                                                    {puedeGestionar && (
                                                        <button
                                                            onClick={() => handleCerrarTurno(fichaje)}
                                                            disabled={cerrandoId === (fichaje.id || fichaje._id)}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-600 hover:text-white transition-colors disabled:opacity-50"
                                                        >
                                                            {cerrandoId === (fichaje.id || fichaje._id) ? 'Cerrando…' : 'Cerrar turno'}
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-500 border border-slate-200">
                                                    Cerrado
                                                </span>
                                            )}
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            <div className="inline-flex flex-col items-center gap-1.5">
                                                {fichaje.revisionPendiente ? (
                                                    <div
                                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold cursor-help"
                                                        title={`${fichaje.auditoria?.motivo || 'El sistema topó la salida al límite legal.'} — La hora de salida es estimada; RRHH debe validar la real.`}
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                                        Revisar RRHH
                                                    </div>
                                                ) : fichaje.cierreAutomatico ? (
                                                    <div
                                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold cursor-help"
                                                        title={fichaje.auditoria?.motivo || 'Cierre forzado por el sistema tras exceder las horas configuradas.'}
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                                        Sistema
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-300 text-xs font-bold">—</span>
                                                )}
                                                {/* Editar horas: disponible en cualquier turno CERRADO para gestores */}
                                                {puedeGestionar && fichaje.estado !== 'abierto' && (
                                                    <button
                                                        onClick={() => abrirCorreccion(fichaje)}
                                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide transition-colors ${fichaje.revisionPendiente ? 'bg-slate-900 text-white hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                        {fichaje.revisionPendiente ? 'Corregir' : 'Editar horas'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ✍️ MODAL: corrección de jornada (validación RRHH) */}
            <Modal
                open={!!editando}
                onClose={() => setEditando(null)}
                titulo="Corregir jornada"
                subtitulo={editando ? `${editando.usuario?.nombre || ''} ${editando.usuario?.apellidos || ''}` : ''}
                size="md"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200">
                        <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        <p className="text-xs font-semibold text-amber-800">
                            La salida fue estimada por el sistema. Introduce las horas reales para que el registro sea veraz; quedará validado y auditado.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="corr-entrada" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Entrada real</label>
                            <input id="corr-entrada" type="datetime-local" value={form.entrada}
                                   onChange={(e) => setForm(f => ({ ...f, entrada: e.target.value }))}
                                   className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15" />
                        </div>
                        <div>
                            <label htmlFor="corr-salida" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Salida real</label>
                            <input id="corr-salida" type="datetime-local" value={form.salida}
                                   onChange={(e) => setForm(f => ({ ...f, salida: e.target.value }))}
                                   className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15" />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="corr-motivo" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Motivo (opcional)</label>
                        <input id="corr-motivo" type="text" value={form.motivo} maxLength={200}
                               onChange={(e) => setForm(f => ({ ...f, motivo: e.target.value }))}
                               placeholder="Ej. El profesional confirmó su salida real por teléfono"
                               className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15" />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={() => setEditando(null)} disabled={guardando}
                                className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">
                            Cancelar
                        </button>
                        <button onClick={guardarCorreccion} disabled={guardando}
                                className="px-5 py-2.5 text-sm font-bold text-white bg-clinica-primary hover:bg-blue-700 rounded-xl transition-colors shadow-md min-w-[130px] disabled:opacity-60">
                            {guardando ? 'Guardando…' : 'Validar jornada'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
